import { colors } from '@fikasio/styles';
import React, { useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import { Icon } from '@fikasio/react-ui-components';
import { useTranslation } from 'react-i18next';
import LinkifiedText from 'components/UI/LinkifiedText';
import TrashIcon from 'components/UI/TrashIcon';
import UserAvatar from 'components/UI/UserAvatar';
import { isCommentEdited } from 'services/comments/isEdited';
import { COMMENT_MAX_LENGTH } from 'services/comments/limits';
import { Comment } from 'services/comments/types';
import { assigneeDisplayName } from 'services/users/assignable';
import { toDateTime } from 'utils/date';
import styles from './Discussion.module.css';

export interface DiscussionUpdate {
  createdAt: Date | string;
  id: string;
  summary: string;
}

interface DiscussionProps {
  comments?: Comment[];
  currentUserId?: string;
  isLoading?: boolean;
  onCreate: (comment: string) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  onUpdate: (id: string, comment: string) => Promise<unknown>;
  updates?: DiscussionUpdate[];
}

type FeedItem = {
  createdAt: Date | string;
  id: string;
} & ({
  comment: Comment;
  kind: 'comment';
} | {
  kind: 'update';
  summary: string;
});

const Discussion = ({
  comments = [],
  currentUserId,
  isLoading = false,
  onCreate,
  onDelete,
  onUpdate,
  updates,
}: DiscussionProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const tooLongMessage = (value: string) => (
    value.trim().length > COMMENT_MAX_LENGTH
      ? t('commentTooLong', { max: COMMENT_MAX_LENGTH })
      : null
  );
  const draftTooLong = tooLongMessage(draft);
  const editTooLong = tooLongMessage(editingValue);

  const submitDraft = async () => {
    const comment = draft.trim();
    if (!comment || isSaving || draftTooLong) {
      return;
    }
    setIsSaving(true);
    setComposerError(null);
    try {
      await onCreate(comment);
      setDraft('');
    } catch {
      setComposerError(t('couldNotSaveComment'));
    } finally {
      setIsSaving(false);
    }
  };

  const saveEdit = async () => {
    const comment = editingValue.trim();
    if (!editingId || !comment || isSaving || editTooLong) {
      return;
    }
    setIsSaving(true);
    setEditError(null);
    try {
      await onUpdate(editingId, comment);
      setEditingId(null);
      setEditingValue('');
    } catch {
      setEditError(t('couldNotSaveComment'));
    } finally {
      setIsSaving(false);
    }
  };

  const feed: FeedItem[] = [
    ...comments.map(comment => ({
      comment,
      createdAt: comment.createdAt,
      id: `comment-${comment.id}`,
      kind: 'comment' as const,
    })),
    ...(updates ?? []).map(update => ({
      createdAt: update.createdAt,
      id: `update-${update.id}`,
      kind: 'update' as const,
      summary: update.summary,
    })),
  ].sort((itemA, itemB) => {
    const aTime = toDateTime(itemA.createdAt)?.toMillis() ?? 0;
    const bTime = toDateTime(itemB.createdAt)?.toMillis() ?? 0;
    return aTime - bTime;
  });
  const isEmpty = comments.length === 0 && (updates ?? []).length === 0;

  return (
    <div className={styles.discussion}>
      <div className={styles.list}>
        {
          !isLoading && isEmpty && (
            <div className={styles.empty}>
              {updates ? t('noActivityToDisplay') : t('noCommentsToDisplay')}
            </div>
          )
        }
        {
          feed.map(item => {
            if (item.kind === 'update') {
              return (
                <div
                  className={styles.update}
                  key={item.id}
                >
                  <span>{item.summary}</span>
                  <span className={styles.time}>
                    {toDateTime(item.createdAt)?.toRelative() || ''}
                  </span>
                </div>
              );
            }
            const message = item.comment;
            const authorName = assigneeDisplayName(message.author) || t('unknownUser');
            const isOwn = Boolean(currentUserId && message.userId === currentUserId);
            const isEditing = editingId === message.id;
            return (
              <div
                className={styles.comment}
                key={item.id}
              >
                <UserAvatar
                  alt={authorName}
                  hasProfilePicture={message.author.hasProfilePicture}
                  modifiedAt={message.author.modifiedAt}
                  size={32}
                  userId={message.author.id}
                />
                <div className={styles.body}>
                  <div className={styles.meta}>
                    <span className={styles.author}>{authorName}</span>
                    <span className={styles.time}>
                      {toDateTime(message.createdAt)?.toRelative() || ''}
                      {isCommentEdited(message) ? ` (${t('edited')})` : ''}
                    </span>
                    {
                      isOwn && !isEditing && (
                        <span className={styles.actions}>
                          <Icon
                            name="edit"
                            onClick={() => {
                              setEditingId(message.id);
                              setEditingValue(message.comment);
                            }}
                            size="1x"
                            style={{ color: 'blue', cursor: 'pointer' }}
                          />
                          <TrashIcon
                            onClick={() => onDelete(message.id)}
                            size="1x"
                            style={{ color: colors.danger, cursor: 'pointer' }}
                          />
                        </span>
                      )
                    }
                  </div>
                  {
                    isEditing ? (
                      <>
                        {(editTooLong || editError) && (
                          <Alert variant="danger">
                            {editTooLong || editError}
                          </Alert>
                        )}
                        <textarea
                          className="form-control"
                          onChange={event => {
                            setEditingValue(event.target.value);
                            setEditError(null);
                          }}
                          rows={3}
                          value={editingValue}
                        />
                        <div className={styles.composerActions}>
                          <Button
                            disabled={isSaving || !editingValue.trim() || Boolean(editTooLong)}
                            onClick={saveEdit}
                            size="sm"
                            variant="primary"
                          >
                            {t('save')}
                          </Button>
                          <Button
                            onClick={() => {
                              setEditingId(null);
                              setEditingValue('');
                            }}
                            size="sm"
                            variant="link"
                          >
                            {t('cancel')}
                          </Button>
                        </div>
                      </>
                    ) : (
                      <div className={styles.text}>
                        <LinkifiedText text={message.comment} />
                      </div>
                    )
                  }
                </div>
              </div>
            );
          })
        }
      </div>
      <div className={styles.composer}>
        {(draftTooLong || composerError) && (
          <Alert variant="danger">
            {draftTooLong || composerError}
          </Alert>
        )}
        <textarea
          className="form-control"
          onChange={event => {
            setDraft(event.target.value);
            setComposerError(null);
          }}
          onKeyDown={event => {
            if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
              event.preventDefault();
              submitDraft();
            }
          }}
          placeholder={t('writeAComment')}
          rows={3}
          value={draft}
        />
        <div className={styles.composerActions}>
          <Button
            disabled={isSaving || !draft.trim() || Boolean(draftTooLong)}
            onClick={submitDraft}
            variant="primary"
          >
            {t('addComment')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Discussion;
